import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import timeAgo from '../../../../../helpers/timeAgo';
import { StudentTeamsDto } from '../../../hooks/api/types';

interface IProps {
  iframe: any;
  studyGroupPk: string;
  displayUserPk?: string;
  setIsSaving: (isSaving: boolean) => void;
}

const useAssessment = (props: IProps) => {
  const { displayUserPk, iframe, studyGroupPk, setIsSaving } = props;
  const user = useContext(AuthContext);
  const isTeacher = user?.roles.includes(Role.teacher);
  const [studentTeams, setStudentTeams] = useState<any>([]);
  const [currentStudentTeam, setCurrentStudentTeam] = useState<any>([]);
  const [assessment, setAssessment] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isContentChanged, setIsContentChanged] = useState(false);

  const selectedDisplayUserPk = displayUserPk || user?.pk;

  const getQuestionElements = () => {
    const iframeDoc = iframe.current.contentWindow.document as Document;
    const questions =
      iframeDoc.querySelectorAll<HTMLElement>('.question-wrapper');
    return questions;
  };

  const getSaveAllBtn = () => {
    const iframeDoc = iframe.current.contentWindow.document as Document;
    const saveAllBtn =
      iframeDoc.querySelector<HTMLButtonElement>('#save-all-btn');
    return saveAllBtn;
  };

  const hideEditorHeaders = () => {
    const iframeDoc = iframe.current.contentWindow.document as Document;
    iframeDoc
      .querySelectorAll<HTMLDivElement>('.tox-editor-header')
      .forEach((el: any) => {
        el.style.display = 'none';
      });
  };

  const getAssessmentId = () => {
    const iframeDoc = iframe.current.contentWindow.document;
    const assessmentId = parseInt(
      iframeDoc.querySelector('#assessment_id').textContent,
    );
    return assessmentId;
  };

  const getAssessmentData = async () => {
    const assessmentId = getAssessmentId();
    const response = await axios.get(
      `/api/v1/syllabus/assessments/${assessmentId}`,
    );
    setAssessment(response.data[0]);
    return response.data;
  };

  const saveAll = () => {
    const saveAllBtn = getSaveAllBtn();
    saveAllBtn?.click();
    const iframeDoc = iframe.current.contentWindow.document;
    const lastSavedElements = iframeDoc.querySelectorAll('.last-saved');
    lastSavedElements.forEach((el: HTMLElement) => {
      el.innerText = '0 seconds ago';
      setTimeout(() => {
        el.innerText =
          new Date().toLocaleDateString('en-US') +
          ' ' +
          new Date().toLocaleTimeString('en-US');
      }, 5000);
    });
  };

  const onTextAreaChange = () => {
    setIsContentChanged(true);
  };

  const setupTextareas = (addEventListener: boolean) => {
    const iframeDoc = iframe.current.contentWindow.document as Document;
    const textareas = iframeDoc.querySelectorAll('textarea');
    textareas.forEach((textarea) => {
      if (textarea.id.includes('-')) {
        if (addEventListener) {
          textarea.addEventListener('content-change', onTextAreaChange);
        } else {
          textarea.removeEventListener('content-change', onTextAreaChange);
        }
      }
    });
  };

  const setAssessmentAnswers = async (studentTeamId: string) => {
    setupTextareas(false);
    const answers = await getAssessmentAnswers(studentTeamId);
    const questionElements = getQuestionElements();
    questionElements.forEach((questionElement) => {
      const questionId = parseInt(questionElement.id.split('-')[1]!);
      const answer = answers.find((a: any) => a.fields.question === questionId);
      const textarea = questionElement.querySelector('textarea')!;
      const lastSaved = questionElement.querySelector(
        '.last-saved',
      )! as HTMLElement;
      const defaultAnswer = questionElement.querySelector('.default-answer');
      const answerValue = answer
        ? answer.fields.answer
        : defaultAnswer?.innerHTML;
      const updatedAt = answer ? answer.fields.updated_at : null;
      if (updatedAt) {
        lastSaved.innerText = timeAgo(new Date(updatedAt));
      }
      textarea.value = answerValue;
      const event = new CustomEvent('change');
      textarea.dispatchEvent(event);
    });
    if (isTeacher) {
      hideEditorHeaders();
    }
    setupTextareas(true);
  };

  const getAssessmentAnswers = async (studentTeamId: string) => {
    const assessmentId = getAssessmentId();
    const response = await axios.get(
      `/api/v1/syllabus/assessment_answers/?assessment_id=${assessmentId}${
        studentTeamId
          ? `&student_team_id=${studentTeamId}`
          : `&user_id=${selectedDisplayUserPk}`
      }`,
    );
    return response.data;
  };

  const saveAssessmentAnswer = async ({
    answer,
    questionId,
    studentTeamId,
  }: {
    answer: string;
    questionId: number;
    studentTeamId: string;
  }) => {
    const assessmentId = getAssessmentId();
    await axios.post(
      '/api/v1/syllabus/assessment_answers/',
      {
        answer,
        assessment_id: assessmentId,
        question_id: questionId,
        ...(studentTeamId
          ? { student_team_id: studentTeamId }
          : { user_id: selectedDisplayUserPk }),
      },
      {
        headers: {
          'X-CSRFToken': JSON.parse(
            document.getElementById('csrf_token')!.innerHTML,
          ),
        },
      },
    );
  };

  const getStudentTeams = async () => {
    const response = await axios.get<StudentTeamsDto[]>(
      `/api/v1/clients/student_teams/?study_group=${studyGroupPk}`,
    );
    setStudentTeams(response.data);
    return response.data;
  };

  const getStudentTeamId = async () => {
    const teams = await getStudentTeams();
    const team = teams.reduce((acc: any, team: any) => {
      if (team.fields.members.includes(selectedDisplayUserPk)) {
        return team;
      }
      return acc;
    }, null);
    const teamId = team?.pk || null;
    setCurrentStudentTeam(team || null);
    return teamId;
  };

  const setSaveButtonEvents = (studentTeamId: string) => {
    const questionElements = getQuestionElements();
    const saveAllBtn = getSaveAllBtn();
    const saveEvents: (() => Promise<void>)[] = [];
    questionElements.forEach((questionElement) => {
      const questionId = parseInt(questionElement.id.split('-')[1]!);
      const saveButton =
        questionElement.querySelector<HTMLButtonElement>('.save-btn');
      if (!saveButton) {
        return;
      }
      if (isTeacher) {
        saveButton.style.display = 'none';
        return;
      }
      saveButton.disabled = true;
      saveButton.style.display = 'none';
      saveButton.style.marginBottom = '-48px';
      const textarea = questionElement.querySelector('textarea')!;
      const saveEvent = async () => {
        saveButton.click();
        saveButton.style.display = 'block';
        saveButton.innerText = 'Saving...';
        saveButton.disabled = true;
        try {
          await saveAssessmentAnswer({
            answer: textarea.value,
            questionId,
            studentTeamId,
          });
          saveButton.innerText = 'Save';
          saveButton.disabled = true;
        } catch (e) {
          saveButton.innerText = 'Something went wrong... Try again.';
          saveButton.disabled = false;
          throw Error('Something went wrong... Try again.');
        }
        saveButton.style.display = 'none';
      };
      saveEvents.push(saveEvent);
      saveButton.addEventListener('click', saveEvent);
    });
    saveAllBtn?.addEventListener('click', async () => {
      setIsSaving(true);
      try {
        await Promise.all(
          saveEvents.map(async (saveEvent) => await saveEvent()),
        );
        setIsContentChanged(false);
      } catch (e) {
        alert(e);
      }
      setIsSaving(false);
    });
    if (isTeacher && saveAllBtn) {
      saveAllBtn.style.display = 'none';
      return;
    }
  };

  const initAssessments = async () => {
    try {
      getAssessmentId();
    } catch (e) {
      setTimeout(initAssessments, 500);
      return;
    }
    setIsLoading(true);
    const assessments = await getAssessmentData();
    let studentTeamId = null;
    if (assessments[0].fields.is_group) {
      studentTeamId = await getStudentTeamId();
    }
    await setAssessmentAnswers(studentTeamId);
    setSaveButtonEvents(studentTeamId);
    setIsLoading(false);
  };

  useEffect(() => {
    getStudentTeams();
    initAssessments();
  }, [selectedDisplayUserPk]);

  return {
    studentTeams,
    assessment,
    currentStudentTeam,
    isLoading,
    isContentChanged,
    saveAll,
  };
};

export default useAssessment;
